import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from '../offline/profileApi';
import { isProfileQueued } from '../offline/profileActions';
import { cacheOwner } from '../offline/profileCache';
import { Alert, Button, Card, Checkbox, Col, Empty, Form, Input, InputNumber, List, Modal, Radio, Row, Space, Spin, Tag, Typography, message } from 'antd';
import { FileDoneOutlined, PlusOutlined, PrinterOutlined } from '@ant-design/icons';
import API from '../config/api';
import { auth } from '../firebaseConfig';
import { resolveAssessmentPatientId } from '../utils/assessmentContext';

const { Text, Title } = Typography;
const { TextArea } = Input;

type AssessmentField = {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'single' | 'multi';
  options?: string[];
  readOnly?: boolean;
  autoFill?: string;
  min?: number;
  max?: number;
  maxSelections?: number;
};

type AssessmentSection = { id: string; number?: number | string; title: string; fields: AssessmentField[] };
type AssessmentTemplate = { id: string; name: string; version: number; sections: AssessmentSection[] };
type AssessmentResponse = {
  assessment_id: string;
  template_id: string;
  template_version: number;
  status: 'draft' | 'completed';
  answers: Record<string, unknown>;
  createdAt: string;
};

type Props = {
  patient: any;
  doctorId: string;
  clinicId: string;
  readOnly?: boolean;
};

const esc = (value: unknown): string => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const normalizeGender = (value: unknown): string => {
  const clean = String(value || '').trim().toLowerCase();
  if (['m', 'male', 'ذكر'].includes(clean)) return 'M';
  if (['f', 'female', 'أنثى', 'انثى'].includes(clean)) return 'F';
  return '';
};

const normalizeAssessmentAnswers = (values: Record<string, unknown> = {}): Record<string, unknown> => {
  const specialTests = values.special_tests;
  if (!Array.isArray(specialTests) || !specialTests.includes('Other')) return values;
  return {
    ...values,
    special_tests: Array.from(new Set(specialTests.map((option) => option === 'Other' ? 'Other Test' : option))),
  };
};

const initialAnswersFor = (template: AssessmentTemplate, patient: any): Record<string, unknown> => {
  const today = new Date().toISOString().slice(0, 10);
  const values: Record<string, unknown> = {};
  for (const field of template.sections.flatMap((section) => section.fields)) {
    if (field.autoFill === 'patient.patient_name') values[field.key] = patient?.patient_name || patient?.name || '';
    else if (field.autoFill === 'patient.age') values[field.key] = patient?.age ?? '';
    else if (field.autoFill === 'patient.gender') values[field.key] = normalizeGender(patient?.gender);
    else if (field.autoFill === 'patient.patient_phone') values[field.key] = patient?.patient_phone || patient?.phone || '';
    else if (field.autoFill === 'today') values[field.key] = today;
    else if (field.type === 'multi') values[field.key] = [];
  }
  return values;
};

const printValue = (field: AssessmentField, value: unknown): string => {
  if (field.type === 'multi') {
    const selected = new Set(Array.isArray(value) ? value.map(String) : []);
    return (field.options || []).map((option) => `${selected.has(option) ? '☑' : '☐'} ${esc(option)}`).join('<span class="option-gap"></span>');
  }
  if (field.type === 'single') {
    return (field.options || []).map((option) => `${String(value || '') === option ? '☑' : '☐'} ${esc(option)}`).join('<span class="option-gap"></span>');
  }
  return esc(value || '—').replace(/\n/g, '<br/>');
};

const buildGenericPrintHtml = (template: AssessmentTemplate, answers: Record<string, unknown>, createdAt?: string): string => {
  const sections = template.sections.map((section, index) => {
    const number = section.number === undefined ? index + 1 : section.number;
    return `
    <section>
      <h2>${number === '' ? '' : `<span>${number}</span>`}${esc(section.title)}</h2>
      <div class="fields">
        ${section.fields.map((field) => `
          <div class="field ${field.type === 'textarea' ? 'wide' : ''}">
            <div class="label">${esc(field.label)}</div>
            <div class="value">${printValue(field, answers[field.key])}</div>
          </div>
        `).join('')}
      </div>
    </section>`;
  }).join('');

  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(template.name)}</title><style>
    @page{size:A4 portrait;margin:8mm}*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#071b42;margin:0}.header{text-align:center;margin-bottom:8px;border-bottom:3px solid #f45b0b;padding-bottom:8px}.header h1{font-size:25px;margin:0}.meta{font-size:10px;color:#666;margin-top:3px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:7px}section{border:1.5px solid #f6c3a7;border-radius:8px;padding:7px;break-inside:avoid}h2{margin:0 0 6px;font-size:13px;color:#fff;background:#f45b0b;border-radius:6px;padding:5px 7px}h2 span{display:inline-block;margin-right:5px}.fields{display:grid;grid-template-columns:1fr 1fr;gap:5px 8px}.field{min-height:29px;border-bottom:1px solid #cbd5e1;padding:2px}.field.wide{grid-column:1/-1}.label{font-size:9px;font-weight:700}.value{font-size:10px;line-height:1.35;overflow-wrap:anywhere}.option-gap{display:inline-block;width:8px}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
  </style></head><body><div class="header"><h1>${esc(template.name)}</h1><div class="meta">Template version ${template.version}${createdAt ? ` | ${esc(new Date(createdAt).toLocaleString())}` : ''}</div></div><main class="grid">${sections}</main></body></html>`;
};

const AssessmentFormsSection: React.FC<Props> = ({ patient, doctorId, clinicId, readOnly = false }) => {
  const featureEnabled = process.env.REACT_APP_ASSESSMENT_FORMS_ENABLED === 'true';
  const [templates, setTemplates] = useState<AssessmentTemplate[]>([]);
  const [responses, setResponses] = useState<AssessmentResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<AssessmentTemplate | null>(null);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});

  const patientId = resolveAssessmentPatientId(patient);

  const authenticatedHeaders = useCallback(async () => {
    if (!navigator.onLine && cacheOwner()) return {};
    await auth.authStateReady();
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error('Your doctor login is not ready. Refresh the page or sign in again.');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!featureEnabled || !doctorId || !clinicId || !patientId) {
      setTemplates([]);
      setResponses([]);
      setLoadError('');
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const headers = await authenticatedHeaders();
      const params = { doctor_id: doctorId, clinic_id: clinicId };
      const templateResult = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.ASSESSMENT_FORM_TEMPLATES}`, { params, headers, signal });
      if (signal?.aborted) return;
      const availableTemplates = templateResult.data?.data || [];
      setTemplates(availableTemplates);
      if (availableTemplates.length > 0) {
        const responseResult = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.ASSESSMENT_FORM_RESPONSES}`, {
          params: { ...params, patient_id: patientId }, headers, signal,
        });
        if (signal?.aborted) return;
        setResponses(responseResult.data?.data || []);
      } else {
        setResponses([]);
      }
    } catch (error: any) {
      if (axios.isCancel(error) || signal?.aborted) return;
      setTemplates([]);
      setResponses([]);
      const status = error?.response?.status;
      if (status === 403) {
        setLoadError('');
        return;
      }
      console.warn('Failed to load assessment forms:', error);
      setLoadError(error?.response?.data?.message || error?.message || 'Could not load assessment forms.');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [authenticatedHeaders, clinicId, doctorId, featureEnabled, patientId]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const templateById = useMemo(() => new Map(templates.map((template) => [template.id, template])), [templates]);

  const openNew = (template: AssessmentTemplate) => {
    setSelectedTemplate(template);
    setSelectedAssessmentId(null);
    setAnswers(initialAnswersFor(template, patient));
    setViewOnly(false);
    setModalOpen(true);
  };

  const openSaved = (response: AssessmentResponse, editable = false) => {
    const template = templateById.get(response.template_id);
    if (!template) return;
    setSelectedTemplate(template);
    setSelectedAssessmentId(response.assessment_id);
    setAnswers(normalizeAssessmentAnswers(response.answers || {}));
    setViewOnly(readOnly || !editable);
    setModalOpen(true);
  };

  const setAnswer = (key: string, value: unknown) => setAnswers((current) => ({ ...current, [key]: value }));

  const save = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      const headers = await authenticatedHeaders();
      const payload = {
        doctor_id: doctorId,
        clinic_id: clinicId,
        patient_id: patientId,
        visit_id: patient?.visit_id || patient?.current_visit_id || '',
        template_id: selectedTemplate.id,
        status: 'completed',
        answers,
      };
      if (selectedAssessmentId) {
        await axios.put(`${API.BASE_URL}${API.ENDPOINTS.ASSESSMENT_FORM_RESPONSES}/${selectedAssessmentId}`, payload, { headers });
      } else {
        await axios.post(`${API.BASE_URL}${API.ENDPOINTS.ASSESSMENT_FORM_RESPONSES}`, payload, { headers });
      }
      message.success(selectedAssessmentId ? 'Assessment updated' : 'Assessment saved');
      setModalOpen(false);
      await load();
    } catch (error: any) {
      if (isProfileQueued(error)) { message.warning('Assessment saved on this device — pending sync.'); setModalOpen(false); return; }
      message.error(error?.response?.data?.message || 'Could not save assessment');
    } finally {
      setSaving(false);
    }
  };

  const print = async (template: AssessmentTemplate, values: Record<string, unknown>, createdAt?: string) => {
    const win = window.open('', '_blank');
    if (!win) return;
    try {
      const {
        buildBrandedAssessmentPrintHtml,
        getBrandedAssessmentPrintAssets,
        hasBrandedAssessmentPrintLayout,
      } = await import('../utils/assessmentPrintTemplate');
      const usesBrandedTemplate = hasBrandedAssessmentPrintLayout(template.id);
      win.document.write(usesBrandedTemplate
        ? buildBrandedAssessmentPrintHtml(template, values, window.location.origin)
        : buildGenericPrintHtml(template, values, createdAt));
      win.document.close();
      const expectedAssets = usesBrandedTemplate ? getBrandedAssessmentPrintAssets(template.id).length : 0;
      const images = Array.from(win.document.images);
      const ready = images.map((image) => image.complete
        ? Promise.resolve(image.naturalWidth > 0)
        : new Promise<boolean>((resolve) => {
          image.addEventListener('load', () => resolve(true), { once: true });
          image.addEventListener('error', () => resolve(false), { once: true });
        }));
      const loaded = await Promise.all(ready);
      if (expectedAssets > 0 && (images.length < expectedAssets || loaded.some((success) => !success))) {
        message.error('The assessment print template could not be loaded.');
        win.close();
        return;
      }
      win.focus();
      setTimeout(() => win.print(), 100);
    } catch (error) {
      console.warn('Failed to prepare assessment print:', error);
      win.close();
      message.error('The assessment print template could not be loaded.');
    }
  };

  const renderField = (field: AssessmentField) => {
    const value = answers[field.key];
    const disabled = viewOnly || field.readOnly;
    if (field.type === 'textarea') {
      return <TextArea rows={2} value={String(value || '')} disabled={disabled} onChange={(event) => setAnswer(field.key, event.target.value)} />;
    }
    if (field.type === 'number') {
      return <InputNumber style={{ width: '100%' }} value={value === '' || value === undefined ? null : Number(value)} min={field.min} max={field.max} disabled={disabled} onChange={(next) => setAnswer(field.key, next ?? '')} />;
    }
    if (field.type === 'single') {
      return <Radio.Group value={value} disabled={disabled} onChange={(event) => setAnswer(field.key, event.target.value)} options={(field.options || []).map((option) => ({ label: option, value: option }))} />;
    }
    if (field.type === 'multi') {
      return (
        <Checkbox.Group
          value={Array.isArray(value) ? value as string[] : []}
          disabled={disabled}
          options={field.options || []}
          onChange={(next) => {
            if (field.maxSelections && next.length > field.maxSelections) {
              message.warning(`Choose up to ${field.maxSelections}`);
              return;
            }
            setAnswer(field.key, next);
          }}
        />
      );
    }
    return <Input value={String(value || '')} disabled={disabled} onChange={(event) => setAnswer(field.key, event.target.value)} />;
  };

  if (!featureEnabled) return null;
  if (loading) return <Card size="small" style={{ marginBottom: 20 }}><Spin size="small" /> Loading assessment forms…</Card>;
  if (loadError) return <Alert type="warning" showIcon message="Assessment Forms" description={loadError} style={{ marginBottom: 20 }} />;
  if (templates.length === 0) return null;

  return (
    <Card style={{ marginBottom: 24, borderColor: '#f59e0b' }}>
      <Row justify="space-between" align="middle" gutter={[12, 12]}>
        <Col>
          <Title level={4} style={{ margin: 0 }}><FileDoneOutlined /> Assessment Forms</Title>
          <Text type="secondary">Available only for this doctor in the selected clinic</Text>
        </Col>
        {!readOnly && (
          <Col>
            <Space wrap>
              {templates.map((template) => (
                <Button key={template.id} icon={<PlusOutlined />} onClick={() => openNew(template)}>{template.name}</Button>
              ))}
            </Space>
          </Col>
        )}
      </Row>

      <div style={{ marginTop: 18 }}>
        {responses.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No assessments saved yet" /> : (
          <List
            size="small"
            dataSource={responses}
            rowKey="assessment_id"
            pagination={responses.length > 5 ? { pageSize: 5, showSizeChanger: false, size: 'small' } : false}
            renderItem={(response) => {
              const template = templateById.get(response.template_id);
              if (!template) return null;
              return (
                <List.Item actions={[
                  <Button key="view" type="link" onClick={() => openSaved(response)}>View</Button>,
                  ...(!readOnly ? [<Button key="edit" type="link" onClick={() => openSaved(response, true)}>Edit</Button>] : []),
                  <Button key="print" type="link" icon={<PrinterOutlined />} onClick={() => print(template, response.answers, response.createdAt)}>Print</Button>,
                ]}>
                  <List.Item.Meta
                    title={template.name}
                    description={new Date(response.createdAt).toLocaleString()}
                  />
                  <Tag color={response.status === 'completed' ? 'green' : 'orange'}>{response.status}</Tag>
                </List.Item>
              );
            }}
          />
        )}
      </div>

      <Modal
        open={modalOpen}
        title={selectedTemplate?.name}
        width={980}
        onCancel={() => setModalOpen(false)}
        footer={selectedTemplate ? [
          <Button key="cancel" onClick={() => setModalOpen(false)}>Close</Button>,
          <Button key="print" icon={<PrinterOutlined />} onClick={() => print(selectedTemplate, answers)}>Print</Button>,
          ...(!viewOnly && !readOnly ? [<Button key="save" type="primary" loading={saving} onClick={save}>Save assessment</Button>] : []),
        ] : null}
      >
        {selectedTemplate && (
          <Form layout="vertical">
            <Row gutter={[14, 14]}>
              {selectedTemplate.sections.map((section, sectionIndex) => (
                <Col xs={24} lg={12} key={section.id}>
                  <Card
                    size="small"
                    title={`${section.number === undefined ? sectionIndex + 1 : section.number}${section.number === '' ? '' : '. '}${section.title}`}
                    styles={{ header: { background: '#fff7ed' } }}
                  >
                    {section.fields.map((field) => (
                      <Form.Item key={field.key} label={field.label}>
                        {renderField(field)}
                      </Form.Item>
                    ))}
                  </Card>
                </Col>
              ))}
            </Row>
          </Form>
        )}
      </Modal>
    </Card>
  );
};

export default React.memo(AssessmentFormsSection);
